import type { init } from '@sentry/nextjs';

type TransactionEvent = Parameters<
  NonNullable<NonNullable<Parameters<typeof init>[0]>['beforeSendTransaction']>
>[0];

export type LastRoute = { route: string; endedAt: number };

const UUID_SEGMENT =
  /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;

// Before Next resolves the route, the SDK may still hold the raw URL; ids in a
// tag would make `nav_from` unbounded. Parameterized names pass through.
export function routeName(transaction: string | undefined): string {
  return (transaction ?? 'unknown').replace(UUID_SEGMENT, '/:id');
}

// Sentry keeps `sentry.previous_trace` on every navigation but cannot join on
// it in a query, so "which tab do users open after landing on X" and "how long
// did they stay on X" are unanswerable there. Stamping the previous route as a
// tag and the dwell as a measurement makes both a single group-by.
export function annotateNavigation(
  event: TransactionEvent,
  last: LastRoute | null,
): { event: TransactionEvent; last: LastRoute | null } {
  const op = event.contexts?.trace?.op;
  if (op !== 'navigation' && op !== 'pageload') {
    return { event, last };
  }

  const startedAt = (event.start_timestamp ?? 0) * 1000;
  const endedAt = (event.timestamp ?? event.start_timestamp ?? 0) * 1000;

  if (last && op === 'navigation') {
    event.tags = { ...event.tags, nav_from: last.route };
    event.measurements = {
      ...event.measurements,
      nav_dwell: {
        value: Math.max(0, Math.round(startedAt - last.endedAt)),
        unit: 'millisecond',
      },
    };
  }

  return {
    event,
    last: { route: routeName(event.transaction), endedAt },
  };
}
